import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Paper,
  Grid,
  Avatar,
  Typography,
  Button,
  Tooltip,
  Collapse,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import ConfirmationDialog from "../confirm/confirmation"; // Assuming you have a confirmation dialog component
import toast from "react-hot-toast";

const CommentDialogPopup = ({ open, onClose, comments, onDelete, onReply }) => {
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [isReplyDelete, setIsReplyDelete] = useState(false);
  const [expandedCommentIds, setExpandedCommentIds] = useState({}); // Track expanded replies by comment ID
  const sortedComments = [...comments].sort(
    (a, b) => dayjs(b.CreatedAt) - dayjs(a.CreatedAt)
  );

  const handleRemoveComment = (commentId, isReply = false) => {
    if (!isReply) {
      const childComment = comments.filter(
        (comment) => comment.Id === commentId
      );
      if (childComment) {
        console.log(childComment);
        const replies = childComment[0].Replies;
        if (Array.isArray(replies)) {
          const numReplies = replies.length;
          if (numReplies) {
            toast.error("Replies must be deleted.");
            return;
          }
        }
      }
    }
    setCommentIdToDelete(commentId);
    setIsReplyDelete(isReply);
    setOpenConfirmDialog(true);
    setExpandedCommentIds({});
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setCommentIdToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (commentIdToDelete) {
      onDelete(commentIdToDelete, isReplyDelete);
      onClose();
    }
    handleCloseConfirmDialog();
  };

  const handleReplySubmit = (replyText) => {
    if (replyToCommentId) {
      onReply(replyToCommentId, replyText);
      setReplyToCommentId(null);
      setExpandedCommentIds({});
      onClose();
    }
  };

  const handleToggleReplies = (commentId) => {
    setReplyToCommentId(commentId);
    setExpandedCommentIds((prev) => ({
      [commentId]: !prev[commentId], // Toggle the current comment and collapse others
    }));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Comments</DialogTitle>
        <DialogContent>
          {sortedComments.map((comment) => (
            <Paper
              key={comment.Id}
              style={{ padding: "20px", marginBottom: "10px" }}
            >
              <Grid container wrap="nowrap" spacing={2}>
                <Grid item>
                  <Avatar style={{ backgroundColor: "#f50057", color: "#fff" }}>
                    {comment.AvatarName}
                  </Avatar>
                </Grid>
                <Grid item xs zeroMinWidth>
                  <Grid container justifyContent="space-between">
                    <Typography variant="h6" style={{ margin: 0 }}>
                      {comment.CreatedBy}
                    </Typography>
                    <Grid item>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          color="error"
                          aria-label="delete"
                          size="medium"
                          onClick={() => handleRemoveComment(comment.Id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          expandedCommentIds[comment.Id]
                            ? "Collapse Replies"
                            : "Expand Replies"
                        }
                        arrow
                      >
                        <IconButton
                          color="secondary"
                          aria-label="toggle-replies"
                          size="medium"
                          onClick={() => handleToggleReplies(comment.Id)}
                        >
                          {expandedCommentIds[comment.Id] ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="subtitle2"
                    style={{ textAlign: "left", color: "gray" }}
                    gutterBottom
                  >
                    Status: {comment.Status || "N/A"} | Approval Type:{" "}
                    {comment.HHDocumentApprovalType || "N/A"}
                  </Typography>
                  <Typography variant="body2" style={{ textAlign: "left" }}>
                    {comment.Comment}
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ textAlign: "left", color: "gray" }}
                  >
                    {dayjs(comment.CreatedAt).format("MMMM D, YYYY h:mm A")}
                  </Typography>
                </Grid>
              </Grid>
              {/* Render replies with collapse/expand */}
              <Collapse
                in={expandedCommentIds[comment.Id]}
                timeout="auto"
                unmountOnExit
              >
                {comment.Replies.sort(
                  (a, b) => dayjs(a.CreatedAt) - dayjs(b.CreatedAt)
                ).map((reply) => (
                  <Paper
                    key={reply.Id}
                    style={{
                      padding: "10px 20px",
                      margin: "10px 0",
                      marginLeft: "20px",
                    }}
                  >
                    <Grid container wrap="nowrap" spacing={2}>
                      <Grid item>
                        <Avatar
                          style={{ backgroundColor: "#3f51b5", color: "#fff" }}
                        >
                          {reply.AvatarName}
                        </Avatar>
                      </Grid>
                      <Grid item xs zeroMinWidth>
                        <Typography
                          variant="subtitle2"
                          style={{ textAlign: "left", color: "gray" }}
                          gutterBottom
                        >
                          {reply.CreatedBy} - Reply
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ textAlign: "left" }}
                        >
                          {reply.Comment}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ textAlign: "left", color: "gray" }}
                        >
                          {dayjs(reply.CreatedAt).format("MMMM D, YYYY h:mm A")}
                        </Typography>
                        <Tooltip title="Delete Reply" arrow>
                          <IconButton
                            color="error"
                            aria-label="delete-reply"
                            size="small"
                            onClick={() => handleRemoveComment(reply.Id, true)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                {/* Reply input form */}

                <Box style={{ marginTop: "10px", marginLeft: "40px" }}>
                  <Typography variant="h6">Reply</Typography>
                  <textarea
                    rows="4"
                    style={{ width: "100%" }}
                    placeholder="Type your reply here..."
                    //onBlur={(e) => handleReplySubmit(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      handleReplySubmit(
                        document.querySelector("textarea").value
                      )
                    }
                    color="primary"
                    disabled={!replyToCommentId}
                  >
                    Submit Reply
                  </Button>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this comment?"
      />
    </>
  );
};

export default CommentDialogPopup;
